import React, {useMemo, useState} from "react";
import i18n from "i18next";
import {persistLang} from "@i18n/language";
import {useModalStore} from "@store/ModalStore";
import "./LanguagePicker.scss";
import {LANGUAGE_COUNTRY_ITEMS, LanguageCountryItem} from "@components/LanguagePicker/LanguagePicker";
import FlagIcon from "@components/FlagIcon/FlagIcon";

type LanguagePickerModalContentProps = {
    items?: LanguageCountryItem[];
};

export default function LanguagePickerModalContent({
                                                       items = LANGUAGE_COUNTRY_ITEMS,
                                                   }: LanguagePickerModalContentProps) {
    const {close} = useModalStore();
    const [loadingId, setLoadingId] = useState<string | null>(null);

    const currentLang = (i18n.resolvedLanguage?.slice(0, 2) ?? i18n.language?.slice(0, 2) ?? "en") as string;

    const sortedItems = useMemo(() => {
        return [...items];
    }, [items]);

    async function onSelect(item: LanguageCountryItem) {
        if (loadingId) return;

        if (item.languageCode === currentLang) {
            close("dismiss");
            return;
        }

        try {
            setLoadingId(item.id);
            persistLang(item.languageCode, "picker");
            await i18n.changeLanguage(item.languageCode);
            close("primary");
        } catch (error) {
            console.error("[LanguagePicker] changeLanguage failed", error);
        } finally {
            setLoadingId(null);
        }
    }

    return (
        <div className="languagePickerModal">
            <div className="languagePickerModal__grid">
                {sortedItems.map((item) => {
                    const isSelected = item.languageCode === currentLang;
                    const isLoading = loadingId === item.id;
                    const isDisabled = loadingId !== null;

                    return (
                        <button
                            key={item.id}
                            type="button"
                            className={[
                                "languagePickerCard",
                                isSelected ? "languagePickerCard--selected" : "",
                                isLoading ? "languagePickerCard--loading" : "",
                            ].join(" ").trim()}
                            onClick={() => onSelect(item)}
                            disabled={isDisabled}
                            aria-pressed={isSelected}
                            aria-label={`Select ${item.countryName}, ${item.languageName}`}
                        >
                            <div className="languagePickerCard__main">
                                <FlagIcon
                                    code={item.flagCode}
                                    alt={item.countryName}
                                    className="languagePickerCard__flag"
                                />

                                <div className="languagePickerCard__text">
                                    <span className="languagePickerCard__country">{item.countryName}</span>
                                    <span className="languagePickerCard__lang">
                                        {item.languageName} ({item.languageCode.toUpperCase()})
                                    </span>
                                </div>
                            </div>

                            {isSelected ? (
                                <span className="languagePickerCard__badge" aria-hidden="true">
                                    ✓
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}